import {
  scaleLinear,
} from 'd3-scale';
import fromPairs from 'lodash-es/fromPairs';
import keyBy from 'lodash-es/keyBy';
import sortBy from 'lodash-es/sortBy';
import {
  ProductClass,
} from '../../graphQL/types/shared';
import {
  formatPCI,
  formatRCA,
  formatTradeValue,
} from '../../numberFormatters';
import {
  getProductSearchString,
  IDropdownOption,
  ILoadable,
  LoadableStatus,
  ProductMetadatumLevel,
  toNumericMap,
} from '../../Utils';
import {
  getDisplayedProductCode,
  getProductDropdownLabel,
} from '../../Utils';
import {
  DisplayValue,
  DisplayValueStatus,
  IDetailOverlayRow as IRow,
  NodeSizing,
} from '../../viz/Utils';
import {
  ActiveStatusGetter,
  defaultMaxNodeRadius,
  defaultMinNodeRadius,
  defaultNodeRadius,
  IComputationOutput,
  IConnection,
  IMergeOutput,
  INodeWithTrade,
  IProcessedEdge,
  IProcessedNode,
  IProcessedNodeWithTradeData,
  ISize,
  ITooltipNode,
  RelatedNodesMap,
} from './Utils';

interface IUnsortedDropdownOption {
  id: number;
  shortLabel: string;
  value: number;
  label: string;
  searchString: string;
  code: string;
  worldTrade: number;
  countryTrade: number;
  scaledX: number;
  scaledY: number;
  rca: number;
  connections: IConnection[];
  longLabel: string;
  color: string;
  level: ProductMetadatumLevel;
  pci: number | null;
}

export const getActiveAssigner = (
    minNodeRadius: number, maxNodeRadius: number,
  ) => (
    relatedNodesMapStatus: ILoadable<RelatedNodesMap>,
    availableYearsStatus: ILoadable<number[]>,
    mergeOutputStatus: ILoadable<IMergeOutput>,
    isActive: ActiveStatusGetter,
    nodeSizing: NodeSizing,
    sizeStatus: ILoadable<ISize>,
    productClass: ProductClass,
    year: number,
  ): ILoadable<IComputationOutput> => {

  let result: ILoadable<IComputationOutput>;
  if (mergeOutputStatus.status === LoadableStatus.Initial ||
      availableYearsStatus.status === LoadableStatus.Initial ||
      relatedNodesMapStatus.status === LoadableStatus.Initial ||
      sizeStatus.status === LoadableStatus.Initial) {
    result = {status: LoadableStatus.Initial};
  } else  if (mergeOutputStatus.status === LoadableStatus.Loading ||
              availableYearsStatus.status === LoadableStatus.Loading ||
              relatedNodesMapStatus.status === LoadableStatus.Loading ||
              sizeStatus.status === LoadableStatus.Loading) {
    result = {status: LoadableStatus.Loading};
  } else if (mergeOutputStatus.status === LoadableStatus.NotPresent ||
              availableYearsStatus.status === LoadableStatus.NotPresent ||
              relatedNodesMapStatus.status === LoadableStatus.NotPresent ||
              sizeStatus. status === LoadableStatus.NotPresent) {
    result = {status: LoadableStatus.NotPresent};
  } else {
    const {data: {
      nodeList, total, edgeList,
      maxWorldTrade, minWorldTrade, maxCountryTrade, minCountryTrade,
    }} = mergeOutputStatus;
    const {data: {width, height}} = sizeStatus;

    const {data: relatedNodesMap} = relatedNodesMapStatus;
    const {data: availableYears} = availableYearsStatus;

    let getRadius: (datum: INodeWithTrade) => number;
    // Always use the same radius if no sizing preference is specified:
    // Otherwise, use appropriate scale:
    if (nodeSizing === NodeSizing.None) {
      getRadius = () => defaultNodeRadius;
    } else if (nodeSizing === NodeSizing.CountryTrade) {
      const scale = scaleLinear<number, number>()
                    .domain([minCountryTrade, maxCountryTrade])
                    .range([minNodeRadius, maxNodeRadius]);
      getRadius = ({countryTrade}: INodeWithTrade) => scale(countryTrade);
    } else {
      const scale = scaleLinear<number, number>()
                    .domain([minWorldTrade, maxWorldTrade])
                    .range([minNodeRadius, maxNodeRadius]);
      getRadius = ({worldTrade}: INodeWithTrade) => scale(worldTrade);

    }
    const unsortedNodeListWithStatus: IConnection[] = nodeList.map(node => ({
      ...node,
      active: isActive(node),
      radius: getRadius(node),
    }));

    const unsortedNodeListWithStatusMap = toNumericMap(unsortedNodeListWithStatus, ({id}) => id);

    const nodeListWithConnections: IProcessedNodeWithTradeData[] = unsortedNodeListWithStatus.map(node => {
      const {id} = node;
      const retrievedConnectedNodeIds = relatedNodesMap[id];
      const connectedNodeIds = (retrievedConnectedNodeIds === undefined) ? [] : retrievedConnectedNodeIds;
      const connections = connectedNodeIds.map(nodeId => unsortedNodeListWithStatusMap.get(nodeId)!);
      return {
        ...node,
        connections,
      };
    });
    // Sort in descending order by size so the larger circles don't eclipse the
    // smaller ones:
    let nodeListWithStatus: IProcessedNode[];
    if (nodeSizing === NodeSizing.None) {
      nodeListWithStatus = nodeListWithConnections;
    }  else {
      nodeListWithStatus = sortBy(nodeListWithConnections, ({radius}) => -radius);
    }
    const nodePairs = nodeListWithStatus.map(
      node => ([node.id, node] as [number, IProcessedNode]),
    );
    const nodeMap: Record<string, IProcessedNode> = fromPairs(nodePairs);

    const newEdgeList: IProcessedEdge[] = edgeList.map(
      ({source, target, strength}) => {
        const sourceNode = nodeMap[source];
        const targetNode = nodeMap[target];
        if (sourceNode === undefined) {
          throw new Error('Cannot find sourceNode for source ID ' + source);
        }
        if (targetNode === undefined) {
          throw new Error('Cannot find targetNode for target ID ' + target);
        }
        const uniqueKey = `${productClass}-${sourceNode.id}-${targetNode.id}`;
        return {
          nodes: [sourceNode, targetNode] as [IProcessedNode, IProcessedNode],
          uniqueKey, strength,
        };
      },
    );
    const unsortedDropdownOptions: IUnsortedDropdownOption[] = nodeListWithConnections.map(
      ({shortLabel, code, id, worldTrade, countryTrade, pci,
          scaledX, scaledY, rca, connections, longLabel, color, level}) => {
        const option: IUnsortedDropdownOption = {
          value: id,
          label: getProductDropdownLabel(shortLabel, productClass, code, level),
          searchString: getProductSearchString(shortLabel, productClass, code),
          code, worldTrade, countryTrade, scaledX, scaledY, rca, shortLabel, id,
          connections, longLabel, color, level, pci,
        };
        return option;
      },
    );

    const dropdownOptions: IDropdownOption[] = unsortedDropdownOptions.sort(
      // Implicitly assuming all codes are unique:
      (a, b) => (a.code > b.code) ? 1 : -1,
    ).map(
      ({value, label, searchString}) => ({value, label, searchString}),
    );

    const tooltipMapDataList = unsortedDropdownOptions.map(
      ({shortLabel, code, scaledX, scaledY, countryTrade, pci,
            worldTrade, rca, id, connections, longLabel, color, level}) => {

        const codeInfo: IRow = {
          label: __lexiconText('applicationWide.code'),
          value: getDisplayedProductCode(code, productClass, level),
        };
        const yearInfo: IRow = {label: __lexiconText('applicationWide.year'), value: year};
        const countryTradeInfo: IRow = {
          label: __lexiconText('applicationWide.countryTrade'),
          value: formatTradeValue(countryTrade),
        };
        const worldTradeInfo: IRow = {
          label: __lexiconText('applicationWide.worldTrade'),
          value: formatTradeValue(worldTrade),
        };

        const rcaInfo: IRow = {
          label: __lexiconText('applicationWide.rcaFull'),
          value: formatRCA(rca),
        };

        const pciValue: DisplayValue = (pci === null) ?
                                        {status: DisplayValueStatus.ShowNotAvailable} :
                                        {status: DisplayValueStatus.Show, value: formatPCI(pci)};
        const pciInfo: IRow = {
          label: __lexiconText('applicationWide.pciFull'),
          value: pciValue,
        };

        const detailOverlayInfo: IRow[] = [codeInfo, yearInfo, countryTradeInfo, worldTradeInfo, rcaInfo, pciInfo];
        const output: ITooltipNode = {
          shortLabel, code, scaledX, scaledY,
          detailOverlayInfo, id,
          connections, longLabel, color, level,
        };
        return output;
      },
    );

    const tooltipMap: Record<string, ITooltipNode> = keyBy(tooltipMapDataList, ({id}) => id);

    result = {
      status: LoadableStatus.Present,
      data: {
        nodeList: nodeListWithStatus,
        edgeList: newEdgeList,
        total,
        dropdownOptions,
        tooltipMap,
        availableYears,
        relatedNodesMap,
        width, height,
      },
    };
  }
  return result;

};

export const assignActive = getActiveAssigner(defaultMinNodeRadius, defaultMaxNodeRadius);

export default assignActive;
